import { useEffect, useState } from "react";
import api from "@/lib/api";
import { format, startOfMonth } from "date-fns";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { IndianRupee, ShoppingCart, TrendingUp, Percent, PackagePlus, ReceiptText, CalendarDays } from "lucide-react";

const fmtRs = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const fmtDate = (d) => format(d, "yyyy-MM-dd");

export default function ProfitLoss() {
  const [range, setRange] = useState({ from: startOfMonth(new Date()), to: new Date() });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!range?.from || !range?.to) return;
    setLoading(true);
    api
      .get("/reports/pnl", { params: { from_date: fmtDate(range.from), to_date: fmtDate(range.to) } })
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [range]);

  const profit = data && data.gross_profit >= 0;
  const rangeLabel =
    range?.from && range?.to
      ? `${format(range.from, "dd MMM yyyy")} → ${format(range.to, "dd MMM yyyy")}`
      : range?.from
        ? `${format(range.from, "dd MMM yyyy")} → …`
        : "Select date range";

  return (
    <div className="space-y-5" data-testid="pnl-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Profit &amp; Loss</h1>
          <p className="text-sm text-slate-500">Select any date range from the calendar to check profit/loss.</p>
        </div>

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" data-testid="pnl-date-range-btn" className="h-11 bg-white border-slate-200 hover:border-emerald-500 font-mono-num">
              <CalendarDays className="w-4 h-4 mr-2 text-emerald-700" />
              {rangeLabel}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-white" align="end">
            <Calendar
              data-testid="pnl-calendar"
              mode="range"
              selected={range}
              onSelect={(r) => r && setRange(r)}
              numberOfMonths={2}
              disabled={{ after: new Date() }}
              defaultMonth={range?.from}
            />
          </PopoverContent>
        </Popover>
      </div>

      {loading || !data ? (
        <div className="text-slate-500">Loading…</div>
      ) : (
        <>
          <Card className={`p-6 border-2 ${profit ? "border-emerald-600 bg-emerald-50/40" : "border-red-500 bg-red-50/40"}`} data-testid="pnl-summary-card">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                  Gross {profit ? "Profit" : "Loss"} <span className="normal-case text-slate-400">({rangeLabel})</span>
                </div>
                <div className={`font-mono-num text-4xl font-extrabold mt-1 ${profit ? "text-emerald-900" : "text-red-700"}`}>
                  {profit ? "+" : "-"}{fmtRs(Math.abs(data.gross_profit))}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-slate-500">Margin</div>
                <div className="font-mono-num text-2xl font-bold text-slate-900">{data.margin_percent}%</div>
              </div>
            </div>
          </Card>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <Row tid="pnl-revenue" icon={IndianRupee} label="Sale Revenue" value={fmtRs(data.revenue)} note={`${data.invoice_count} bills`} />
            <Row tid="pnl-cogs" icon={ShoppingCart} label="Cost of Goods Sold" value={fmtRs(data.cogs)} note="Purchase cost of sold items" />
            <Row tid="pnl-margin" icon={Percent} label="Profit Margin" value={`${data.margin_percent}%`} note="(Revenue − Cost) / Revenue" />
            <Row tid="pnl-purchases" icon={PackagePlus} label="Stock Purchased" value={fmtRs(data.stock_purchases)} note="Add Stock entries (selected range)" />
            <Row tid="pnl-bills" icon={ReceiptText} label="Total Bills" value={data.invoice_count} note="" />
            <Row tid="pnl-avg" icon={TrendingUp} label="Avg Bill Value" value={data.invoice_count ? fmtRs(data.revenue / data.invoice_count) : "—"} note="" />
          </div>

          <Card className="p-5 border-slate-200">
            <p className="text-xs text-slate-500 leading-relaxed">
              <span className="font-semibold text-slate-700">Note:</span> Profit = Sale Revenue − Purchase Cost of sold medicines
              (purchase price is snapshotted on every sale). Older bills without recorded cost are counted at ₹0 cost.
              Stock Purchased = total cost of stock added via "Add Stock" in the selected date range.
            </p>
          </Card>
        </>
      )}
    </div>
  );
}

function Row({ icon: Icon, label, value, note, tid }) {
  return (
    <Card className="p-5 border-slate-200" data-testid={tid}>
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium truncate">{label}</div>
          <div className="font-mono-num text-2xl font-extrabold text-slate-900 mt-1">{value}</div>
          {note && <div className="text-xs text-slate-400 mt-1">{note}</div>}
        </div>
        <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-emerald-700" />
        </div>
      </div>
    </Card>
  );
}
